import { err, ok, type Result } from '@shared/result';

/**
 * Un teléfono nacional a diez dígitos.
 *
 * México pasó a marcación de diez dígitos en agosto de 2019: se acabaron los
 * prefijos 01, 044 y 045, y desde entonces **todo** número del país —fijo o
 * celular, local o de larga distancia— son exactamente diez dígitos. Por eso el
 * largo es fijo y no un rango.
 *
 * **Se guarda en crudo y se pinta con formato.** El valor que va a Firestore son
 * los diez dígitos y nada más: es lo que necesita un enlace `tel:`, lo que se
 * puede comparar entre dos registros y lo que una columna de Postgres querría el
 * día de la migración. Los espacios son cosa de la vista, y `formatted` los pone.
 *
 * **Lo que llega del portapapeles se limpia en vez de rechazarse.** Los dos
 * contactos legítimos que hay hoy en producción están capturados como
 * `+52 443 570 2789`: exigirles que borren la lada del país sería inventar un
 * trabajo que la función puede hacer sola. Se descartan los signos, el 52 de
 * país cuando sobra, y el 1 que muchos siguen escribiendo detrás por costumbre
 * de los tiempos del 045.
 */

export type PhoneNumberError =
  | { readonly kind: 'empty' }
  | { readonly kind: 'too-short'; readonly digits: string }
  | { readonly kind: 'too-long'; readonly digits: string }
  | { readonly kind: 'invalid-area-code'; readonly digits: string };

export const PHONE_NUMBER_LENGTH = 10;

const COUNTRY_CODE = '52';

/**
 * Las tres únicas ladas de dos dígitos del país: Ciudad de México,
 * Guadalajara y Monterrey. El resto son de tres, y de ahí salen los dos
 * agrupamientos que usa `formatPhoneDigits`.
 */
const TWO_DIGIT_AREA_CODES: readonly string[] = ['33', '55', '81'];

/**
 * Deja sólo los dígitos que son el número, quitando lo que la gente pega
 * alrededor. No valida: eso es de `PhoneNumber.create`.
 */
export const phoneDigitsOf = (raw: string): string => {
  let digits = raw.replace(/\D/g, '');

  if (digits.length > PHONE_NUMBER_LENGTH && digits.startsWith(COUNTRY_CODE)) {
    digits = digits.slice(COUNTRY_CODE.length);
  }
  // El 1 de «+52 1 55 …», que ya no significa nada desde 2019 y se sigue escribiendo.
  if (digits.length === PHONE_NUMBER_LENGTH + 1 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return digits;
};

/**
 * Agrupa como se escribe en México: `55 1234 5678` con lada de dos dígitos,
 * `443 570 2789` con lada de tres.
 *
 * Formatea también lo incompleto, porque es lo que hace posible la máscara
 * mientras se teclea. Con un solo dígito todavía no se sabe qué lada es, así que
 * no agrupa nada hasta el segundo, que es donde la decisión ya no cambia.
 */
export const formatPhoneDigits = (digits: string): string => {
  const sizes = TWO_DIGIT_AREA_CODES.includes(digits.slice(0, 2)) ? [2, 4, 4] : [3, 3, 4];

  const groups: string[] = [];
  let at = 0;
  for (const size of sizes) {
    if (at >= digits.length) break;
    groups.push(digits.slice(at, at + size));
    at += size;
  }
  // Lo que sobre —sólo puede pasar con un valor sin validar— se queda pegado al final.
  if (at < digits.length) groups.push(digits.slice(at));

  return groups.join(' ');
};

/**
 * Para pintar lo que ya está guardado.
 *
 * Da formato cuando puede y devuelve el original cuando no. Los registros
 * anteriores a esta validación traen de todo —`+52 443 570 2789`, y también
 * `321sdfsdffdgfd`— y reescribir en pantalla algo que no es un teléfono sería
 * peor que enseñarlo tal cual: quien lo lee tiene que poder ver que está mal.
 */
export const formatStoredPhone = (stored: string): string => {
  const digits = phoneDigitsOf(stored);
  return digits.length === PHONE_NUMBER_LENGTH ? formatPhoneDigits(digits) : stored;
};

export class PhoneNumber {
  private constructor(
    /** Los diez dígitos, sin nada más. Es lo que se guarda. */
    readonly value: string,
  ) {}

  static create(raw: string): Result<PhoneNumber, PhoneNumberError> {
    const digits = phoneDigitsOf(raw);

    if (digits.length === 0) return err({ kind: 'empty' });
    if (digits.length < PHONE_NUMBER_LENGTH) return err({ kind: 'too-short', digits });
    if (digits.length > PHONE_NUMBER_LENGTH) return err({ kind: 'too-long', digits });

    /*
     * Ninguna lada del país empieza en 0 ni en 1 —van de 2xx a 9xx, más 33, 55 y
     * 81—, así que esto es lo que separa un número de diez teclas iguales de un
     * teléfono. Es la única regla de contenido: adivinar si una lada existe
     * pediría la tabla del IFT y caducaría sola.
     */
    if (digits[0] === '0' || digits[0] === '1') {
      return err({ kind: 'invalid-area-code', digits });
    }

    return ok(new PhoneNumber(digits));
  }

  /** `55 1234 5678`. Para la pantalla, nunca para guardar. */
  get formatted(): string {
    return formatPhoneDigits(this.value);
  }

  equals(other: PhoneNumber): boolean {
    return this.value === other.value;
  }

  toString(): string {
    return this.value;
  }
}

/** El mensaje que va debajo del campo. Dice qué hacer, no qué pasó. */
export const phoneNumberMessage = (error: PhoneNumberError): string => {
  switch (error.kind) {
    case 'empty':
      return 'Escribe el teléfono a 10 dígitos.';
    case 'too-short':
      return `Faltan ${String(PHONE_NUMBER_LENGTH - error.digits.length)} dígitos.`;
    case 'too-long':
      return 'Un teléfono nacional tiene 10 dígitos.';
    case 'invalid-area-code':
      return 'Ninguna lada empieza en 0 ni en 1. Revisa el número.';
  }
};
